import React, { useCallback, useEffect, useState } from "react";
import {
  ConfigProvider,
  Dropdown,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Spin,
  Table,
  Tag,
} from "antd";
import type { MenuProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CloseOutlined,
  FileTextOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import ClientService, { ClientDto } from "../../services/ClientService";
import EngagementService, { EngagementDto } from "../../services/EngagementService";
import EntityService, { EntityDto } from "../../../EntityView/services/EntityService";
import TierList from "../../../TieringView/components/TierList/TierList";
import { CreateClientModal } from "./CreateClientModal";
import { usePartnershipContext } from "../../../../core/context/PartnershipContext";
import { darkFormTheme, MODAL_STYLES, MODAL_TITLE_STYLE } from "../../../../styles/modalStyles";
import styles from "./ClientList.module.css";

const clientService = new ClientService();

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ── EngagementsTab — receives data from parent, keeps same table shape ────────

interface EngagementsTabProps {
  engagements: EngagementDto[];
  loading: boolean;
  onAdd: () => void;
}

const EngagementsTab: React.FC<EngagementsTabProps> = ({ engagements, loading, onAdd }) => {
  const statusClass = (status: string) => {
    if (status === "Active") return styles.statusActive;
    if (status === "Draft") return styles.statusDraft;
    return styles.statusComplete;
  };

  return (
    <div>
      <div className={styles.tabHeader}>
        <button className={styles.btnAddSmall} onClick={onAdd}>
          + Add Engagement
        </button>
      </div>
      {loading ? (
        <div className={styles.loadingState}>
          <Spin />
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Engagement ID</th>
                <th>Engagement Type</th>
                <th>Description</th>
                <th>Tax Year</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {engagements.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", color: "#94a3b8", padding: "24px" }}>
                    No engagements yet. Click + Add Engagement to create one.
                  </td>
                </tr>
              ) : (
                engagements.map((e) => (
                  <tr key={e.id}>
                    <td>{e.engagementId}</td>
                    <td>{e.engagementType}</td>
                    <td>{e.description}</td>
                    <td>{e.taxYear}</td>
                    <td className={statusClass(e.status)}>{e.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── PartnershipsTab — calls GET /api/entity?clientId={clientId} ──────────────

const PartnershipsTab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [entities, setEntities] = useState<EntityDto[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setSelectedPartnership: setCtxPartnership } = usePartnershipContext();

  useEffect(() => {
    setLoading(true);
    EntityService.getEntitiesByClient(clientId)
      .then(setEntities)
      .catch(() => setEntities([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  const handleEntityClick = (e: EntityDto) => {
    setCtxPartnership({ id: String(e.id), name: e.name, entityType: e.entityType || 'Partnership' });
    navigate(`/tiering/${e.id}`);
  };

  return loading ? (
    <div className={styles.loadingState}>
      <Spin />
    </div>
  ) : (
    <div className={styles.tableWrapper}>
      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Partnership Name</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {entities.length === 0 ? (
            <tr>
              <td colSpan={2} style={{ textAlign: "center", color: "#94a3b8", padding: "24px" }}>
                No partnerships found.
              </td>
            </tr>
          ) : (
            entities.map((e) => (
              <tr
                key={e.id}
                style={{ cursor: "pointer" }}
                onClick={() => handleEntityClick(e)}
              >
                <td style={{ color: "#f26522", textDecoration: "underline" }}>{e.name}</td>
                <td className={styles.statusActive}>Active</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// ── ChartOfAccountsTab — static reference data, unchanged ────────────────────

const ChartOfAccountsTab: React.FC = () => (
  <div className={styles.tableWrapper}>
    <table className={styles.dataTable}>
      <thead>
        <tr>
          <th>Account #</th>
          <th>Account Name</th>
          <th>Type</th>
          <th>Category</th>
          <th>Balance</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>1000</td><td>Cash &amp; Cash Equivalents</td><td>Asset</td><td>Current Asset</td><td style={{ textAlign: "right" }}>$1,245,000</td></tr>
        <tr><td>1100</td><td>Accounts Receivable</td><td>Asset</td><td>Current Asset</td><td style={{ textAlign: "right" }}>$387,500</td></tr>
        <tr><td>1500</td><td>Investment in Partnerships</td><td>Asset</td><td>Non-Current</td><td style={{ textAlign: "right" }}>$8,750,000</td></tr>
        <tr><td>1800</td><td>Property &amp; Equipment</td><td>Asset</td><td>Fixed Asset</td><td style={{ textAlign: "right" }}>$2,100,000</td></tr>
        <tr><td>2000</td><td>Accounts Payable</td><td>Liability</td><td>Current Liability</td><td style={{ textAlign: "right" }}>$215,400</td></tr>
        <tr><td>2500</td><td>Notes Payable</td><td>Liability</td><td>Long-Term</td><td style={{ textAlign: "right" }}>$1,500,000</td></tr>
        <tr><td>3000</td><td>Partners&apos; Capital</td><td>Equity</td><td>Capital</td><td style={{ textAlign: "right" }}>$9,200,000</td></tr>
        <tr><td>4000</td><td>Partnership Income</td><td>Revenue</td><td>Income</td><td style={{ textAlign: "right" }}>$3,450,000</td></tr>
        <tr><td>4500</td><td>Interest Income</td><td>Revenue</td><td>Income</td><td style={{ textAlign: "right" }}>$125,000</td></tr>
        <tr><td>5000</td><td>Management Fees</td><td>Expense</td><td>Operating</td><td style={{ textAlign: "right" }}>$480,000</td></tr>
        <tr><td>5500</td><td>Depreciation Expense</td><td>Expense</td><td>Operating</td><td style={{ textAlign: "right" }}>$210,000</td></tr>
        <tr><td>6000</td><td>Tax Provision</td><td>Expense</td><td>Tax</td><td style={{ textAlign: "right" }}>$340,000</td></tr>
      </tbody>
    </table>
  </div>
);

// ── CalcProfilesTab — no backend endpoint exists for calculation profiles yet ─

const CalcProfilesTab: React.FC = () => (
  <div className={styles.emptyState}>
    <div className={styles.emptyIcon}>⚙️</div>
    <div className={styles.emptyText}>Calculation Profiles — coming soon</div>
    <div className={styles.emptySubtext}>
      {/* TODO: implement calculation profiles endpoint and wire this tab */}
      No backend endpoint exists for calculation profiles yet.
    </div>
  </div>
);

// ── DetailsTab — real ClientDto fields; schema gaps shown as disabled ─────────

const DetailsTab: React.FC<{ client: ClientDto }> = ({ client }) => (
  <div className={styles.detailsTabContent}>
    <div className={styles.infoCard}>
      <h3 className={styles.infoCardTitle}>Client Information</h3>
      <div className={styles.fieldGrid}>
        <div className={styles.fieldItem}>
          <div className={styles.fieldLabel}>Client Name</div>
          <div className={styles.fieldValue}>{client.name}</div>
        </div>
        <div className={styles.fieldItem}>
          <div className={styles.fieldLabel}>Client Code</div>
          <div className={styles.fieldValue}>{client.nuemonic || "—"}</div>
        </div>
        <div className={styles.fieldItem}>
          <div className={styles.fieldLabel}>Status</div>
          <div className={styles.fieldValue}>
            <Tag
              color={client.isActive ? "#22c55e" : "#f59e0b"}
              style={{ borderRadius: 4, fontWeight: 500 }}
            >
              {client.isActive ? "Active" : "Inactive"}
            </Tag>
          </div>
        </div>
        <div className={styles.fieldItem}>
          <div className={styles.fieldLabel}>Created</div>
          <div className={styles.fieldValue}>{fmtDate(client.dateCreated)}</div>
        </div>
        {/* Industry: not in ClientDto — show as unavailable */}
        <div className={styles.fieldItemGap}>
          <div className={styles.fieldLabel}>Industry</div>
          <div className={styles.fieldValueGap}>
            — <span className={styles.gapNote}>not in schema</span>
          </div>
        </div>
        {/* Primary Contact: not in ClientDto */}
        <div className={styles.fieldItemGap}>
          <div className={styles.fieldLabel}>Primary Contact</div>
          <div className={styles.fieldValueGap}>
            — <span className={styles.gapNote}>not in schema</span>
          </div>
        </div>
        {/* Contact Email: not in ClientDto */}
        <div className={styles.fieldItemGap}>
          <div className={styles.fieldLabel}>Contact Email</div>
          <div className={styles.fieldValueGap}>
            — <span className={styles.gapNote}>not in schema</span>
          </div>
        </div>
        {/* Contact Phone: not in ClientDto */}
        <div className={styles.fieldItemGap}>
          <div className={styles.fieldLabel}>Contact Phone</div>
          <div className={styles.fieldValueGap}>
            — <span className={styles.gapNote}>not in schema</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ── ProjectsTab — placeholder; no project endpoint scoped to client tenant ────

const ProjectsTab: React.FC = () => (
  <div className={styles.emptyState}>
    <div className={styles.emptyIcon}>📁</div>
    <div className={styles.emptyText}>
      Projects are not yet scoped per client.
    </div>
    <div className={styles.emptySubtext}>
      {/* TODO: implement GET /api/project/list?clientTenantId={id} when backend supports it */}
      This panel will populate once the project-listing endpoint supports filtering by client tenant.
    </div>
  </div>
);

// ── AuditLogTab — placeholder; no client-level audit entity in backend ────────

const AuditLogTab: React.FC = () => (
  <div className={styles.emptyState}>
    <div className={styles.emptyIcon}><FileTextOutlined style={{ fontSize: 32, color: '#ADADAD' }} /></div>
    <div className={styles.emptyText}>No audit history available.</div>
    <div className={styles.emptySubtext}>
      {/* TODO: implement client-level audit log entity and GET /api/client/{id}/audit endpoint */}
      Client-level audit logging is not yet implemented in the backend.
    </div>
  </div>
);

// ── Tab definitions ───────────────────────────────────────────────────────────
// "Engagements" is default and must stay first — tests assert "Engagement ID" on load.
// "Partnerships" label must be kept — tests assert clicking it shows "Partnership Name".

const DETAIL_TABS = [
  { key: "engagements", label: "Engagements" },
  { key: "partnerships", label: "Partnerships" },
  { key: "details", label: "Details" },
  { key: "projects", label: "Projects" },
  { key: "tiering", label: "Tiering Relationship" },
  { key: "coa", label: "Chart of Accounts" },
  { key: "calcProfiles", label: "Calculation Profiles" },
  { key: "auditLog", label: "Audit Log" },
];

// ── ClientDetailPanel ─────────────────────────────────────────────────────────

interface DetailPanelProps {
  client: ClientDto;
  onClose: () => void;
  onDeactivate: () => void;
}

const ClientDetailPanel: React.FC<DetailPanelProps> = ({
  client,
  onClose,
  onDeactivate,
}) => {
  const [activeTab, setActiveTab] = useState("engagements");
  const [engagements, setEngagements] = useState<EngagementDto[]>([]);
  const [engLoading, setEngLoading] = useState(false);
  const [engModalOpen, setEngModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    setEngLoading(true);
    EngagementService.getEngagements(client.id)
      .then(setEngagements)
      .catch(() => setEngagements([]))
      .finally(() => setEngLoading(false));
  }, [client.id]);

  const activeEngCount = engLoading
    ? null
    : engagements.filter((e) => e.status === "Active").length;

  const handleCreateEng = async () => {
    let values: { engagementType: string; description: string; taxYear: number };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const created = await EngagementService.createEngagement(values, client.id);
      setEngagements((prev) => [...prev, created]);
      form.resetFields();
      setEngModalOpen(false);
      message.success("Engagement created");
    } catch {
      message.error("Failed to create engagement");
    } finally {
      setSubmitting(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "engagements":
        return (
          <EngagementsTab
            engagements={engagements}
            loading={engLoading}
            onAdd={() => setEngModalOpen(true)}
          />
        );
      case "partnerships":
        return <PartnershipsTab clientId={client.id} />;
      case "details":
        return <DetailsTab client={client} />;
      case "projects":
        return <ProjectsTab />;
      case "tiering":
        // Cross-tenant scoping gap: TierList calls GET /api/tiering/relationships scoped to
        // the caller's tenant header; no client-scoping query param exists on that endpoint.
        return <TierList />;
      case "coa":
        return <ChartOfAccountsTab />;
      case "calcProfiles":
        return <CalcProfilesTab />;
      case "auditLog":
        return <AuditLogTab />;
      default:
        return null;
    }
  };

  return (
    <aside className={styles.detailPanel}>
      {/* ── Header ── */}
      <div className={styles.detailHeader}>
        <div className={styles.detailTitleBlock}>
          <h2 className={styles.detailName}>{client.name}</h2>
          <div className={styles.detailMeta}>
            <Tag
              color={client.isActive ? "#22c55e" : "#f59e0b"}
              style={{ borderRadius: 4, fontWeight: 500, fontSize: 11 }}
            >
              {client.isActive ? "Active" : "Inactive"}
            </Tag>
            {client.nuemonic && (
              <span className={styles.detailCode}>{client.nuemonic}</span>
            )}
          </div>
        </div>
        <div className={styles.detailHeaderActions}>
          <button className={styles.btnDeactivate} onClick={onDeactivate}>
            {client.isActive ? "Deactivate" : "Reactivate"}
          </button>
          <button className={styles.btnClosePanel} onClick={onClose} aria-label="Close panel">
            <CloseOutlined />
          </button>
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div className={styles.statRow}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Total Entities</div>
          {/* TODO: no endpoint to count entities scoped to a client tenant */}
          <div className={styles.statValue}>—</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Active Engagements</div>
          <div className={styles.statValue}>
            {activeEngCount === null ? <Spin size="small" /> : activeEngCount}
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Recent Projects</div>
          {/* TODO: no endpoint to fetch projects scoped to a client tenant */}
          <div className={styles.statValue}>—</div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className={styles.detailTabs}>
        {DETAIL_TABS.map((t) => (
          <button
            key={t.key}
            className={activeTab === t.key ? styles.detailTabActive : styles.detailTab}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      <div className={styles.detailContent}>{renderTabContent()}</div>

      {/* ── Add Engagement modal ── */}
      <Modal
        title={<span style={MODAL_TITLE_STYLE}>Add Engagement</span>}
        open={engModalOpen}
        onCancel={() => {
          setEngModalOpen(false);
          form.resetFields();
        }}
        onOk={handleCreateEng}
        okText="Create Engagement"
        confirmLoading={submitting}
        destroyOnClose
        styles={MODAL_STYLES}
      >
        <ConfigProvider theme={darkFormTheme}>
          <Form form={form} layout="vertical" initialValues={{ taxYear: 2025 }}>
            <Form.Item
              name="engagementType"
              label="Engagement Type"
              rules={[{ required: true, message: "Please select an engagement type" }]}
            >
              <Select placeholder="Select type">
                <Select.Option value="Compliance">Compliance</Select.Option>
                <Select.Option value="Tax">Tax</Select.Option>
                <Select.Option value="Advisory">Advisory</Select.Option>
                <Select.Option value="Consulting">Consulting</Select.Option>
                <Select.Option value="Audit">Audit</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: "Please enter a description" }]}
            >
              <Input placeholder="e.g. Partnership K-1 preparation" />
            </Form.Item>
            <Form.Item
              name="taxYear"
              label="Tax Year"
              rules={[{ required: true, message: "Please enter a tax year" }]}
            >
              <InputNumber min={2000} max={2100} style={{ width: "100%" }} />
            </Form.Item>
          </Form>
        </ConfigProvider>
      </Modal>
    </aside>
  );
};

// ── FilterPanel — client-side filters (status + date range) ──────────────────
// "Type" and "Has Linked Entities" from mockup spec are hidden — no backend fields.

interface FilterPanelProps {
  statusFilter: "all" | "active" | "inactive";
  setStatusFilter: (v: "all" | "active" | "inactive") => void;
  dateFrom: string;
  setDateFrom: (v: string) => void;
  dateTo: string;
  setDateTo: (v: string) => void;
  onClose: () => void;
  onReset: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  statusFilter,
  setStatusFilter,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  onClose,
  onReset,
}) => (
  <>
    <div className={styles.filterOverlay} onClick={onClose} />
    <aside className={styles.filterPanel}>
      <div className={styles.filterHeader}>
        <h3 className={styles.filterTitle}>Filters</h3>
        <button className={styles.btnClosePanel} onClick={onClose} aria-label="Close filters">
          <CloseOutlined />
        </button>
      </div>

      <div className={styles.filterSection}>
        <div className={styles.filterLabel}>Status</div>
        <div className={styles.statusPills}>
          {(["all", "active", "inactive"] as const).map((v) => (
            <button
              key={v}
              className={statusFilter === v ? styles.pillActive : styles.pill}
              onClick={() => setStatusFilter(v)}
            >
              {v === "all" ? "All" : v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.filterSection}>
        <div className={styles.filterLabel}>Date Created Range</div>
        <div className={styles.dateRow}>
          <input
            className={styles.dateInput}
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <span className={styles.dateSep}>–</span>
          <input
            className={styles.dateInput}
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
      </div>

      {/* Type filter: not in ClientDto — hidden */}
      {/* Has Linked Entities: no backend field — hidden */}
      {/* Note: filtering is applied client-side; the GET /api/client endpoint has no filter params */}

      <div className={styles.filterFooter}>
        <button className={styles.btnOutline} onClick={onReset}>
          Reset
        </button>
        <button className={styles.btnPrimary} onClick={onClose}>
          Apply
        </button>
      </div>
    </aside>
  </>
);

// ── ClientList ────────────────────────────────────────────────────────────────

export const ClientList: React.FC = () => {
  const {
    selectedClient: ctxSelectedClient,
    setSelectedClient: setCtxClient,
    setSelectedPartnership: setCtxPartnership,
  } = usePartnershipContext();

  const [clients, setClients] = useState<ClientDto[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [deactivateTarget, setDeactivateTarget] = useState<ClientDto | null>(null);

  const fetchClients = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await clientService.getAllClients();
      setClients(result);
      if (result.length > 0) {
        setSelectedClient(result[0]);
        setCtxClient({ id: result[0].id, name: result[0].name });
      }
    } catch {
      message.error("Failed to load clients");
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // If context already has a client selected (e.g. navigating back from another screen),
  // sync it to the local panel selection once clients are loaded.
  useEffect(() => {
    if (!ctxSelectedClient || clients.length === 0) return;
    const match = clients.find((c) => c.id === ctxSelectedClient.id);
    if (match) setSelectedClient(match);
  }, [ctxSelectedClient, clients]);

  // All filtering is client-side — GET /api/client has no filter params.
  const filteredClients = clients.filter((c) => {
    if (statusFilter === "active" && !c.isActive) return false;
    if (statusFilter === "inactive" && c.isActive) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (dateFrom && new Date(c.dateCreated) < new Date(dateFrom)) return false;
    if (dateTo && new Date(c.dateCreated) > new Date(dateTo + "T23:59:59")) return false;
    return true;
  });

  const columns: ColumnsType<ClientDto> = [
    {
      title: "Client Name",
      key: "name",
      render: (_, record) => (
        <div>
          <div className={styles.clientName}>{record.name}</div>
          {record.nuemonic && (
            <div className={styles.clientCode}>{record.nuemonic}</div>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      width: 110,
      render: (isActive: boolean) => (
        <Tag
          color={isActive ? "#22c55e" : "#f59e0b"}
          style={{ borderRadius: 4, fontWeight: 500, fontSize: 11 }}
        >
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      // Industry: not in ClientDto — all rows show "—"
      // TODO: add Industry field to ClientDto and ClientCreateDto when backend schema is extended
      title: "Industry",
      key: "industry",
      width: 130,
      render: () => <span className={styles.cellMuted}>—</span>,
    },
    {
      title: "Client Since",
      dataIndex: "dateCreated",
      width: 130,
      render: (date: string) => fmtDate(date),
    },
    {
      title: "",
      key: "actions",
      width: 48,
      render: (_, record) => {
        const items: MenuProps["items"] = [
          {
            key: "select",
            label: "Select",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              setSelectedClient(record);
              setCtxClient({ id: record.id, name: record.name });
              setCtxPartnership(null);
            },
          },
          {
            key: "deactivate",
            label: record.isActive ? "Deactivate" : "Reactivate",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              setDeactivateTarget(record);
            },
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <button
              className={styles.actionBtn}
              onClick={(e) => e.stopPropagation()}
              aria-label="Actions"
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className={styles.pageWrap}>
      {/* ── Table section ── */}
      <div className={styles.tableSection}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Master Clients</h1>
          <button
            className={styles.btnPrimary}
            onClick={() => setShowCreate(true)}
          >
            <PlusOutlined style={{ marginRight: 6 }} />
            Create New Client
          </button>
        </div>

        <div className={styles.filterBar}>
          <div className={styles.searchWrap}>
            <SearchOutlined className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button className={styles.btnOutline} onClick={() => setFilterOpen(true)}>
            <FilterOutlined style={{ marginRight: 6 }} />
            Filters
          </button>
          {statusFilter !== "all" && (
            <span
              className={styles.filterChip}
              onClick={() => setStatusFilter("all")}
            >
              <CloseOutlined style={{ marginRight: 4, fontSize: 10 }} />
              {statusFilter === "active" ? "Active only" : "Inactive only"}
            </span>
          )}
        </div>

        {isLoading ? (
          <div className={styles.loadingState}>
            <Spin />
          </div>
        ) : (
          <Table
            dataSource={filteredClients}
            columns={columns}
            rowKey="id"
            pagination={false}
            onRow={(record) => ({
              onClick: () => {
                setSelectedClient(record);
                setCtxClient({ id: record.id, name: record.name });
                setCtxPartnership(null);
              },
              style: { cursor: "pointer" },
            })}
            rowClassName={(record) =>
              record.id === selectedClient?.id ? "client-row-selected" : ""
            }
            className={styles.clientTable}
            size="small"
          />
        )}

        <div className={styles.tableFooter}>
          Showing {filteredClients.length} of {clients.length} client
          {clients.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* ── Detail panel ── */}
      {selectedClient && (
        <ClientDetailPanel
          key={selectedClient.id}
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
          onDeactivate={() => setDeactivateTarget(selectedClient)}
        />
      )}

      {/* ── Filter panel ── */}
      {filterOpen && (
        <FilterPanel
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
          onClose={() => setFilterOpen(false)}
          onReset={() => {
            setStatusFilter("all");
            setDateFrom("");
            setDateTo("");
          }}
        />
      )}

      {/* ── Create modal ── */}
      <CreateClientModal
        visible={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          fetchClients();
        }}
      />

      {/* ── Deactivate confirmation ── */}
      {/* TODO: add PATCH /api/client/{id}/status or equivalent endpoint to actually deactivate */}
      <Modal
        title={<span style={MODAL_TITLE_STYLE}>{deactivateTarget?.isActive ? "Deactivate client?" : "Reactivate client?"}</span>}
        open={!!deactivateTarget}
        onCancel={() => setDeactivateTarget(null)}
        onOk={() => {
          message.info(
            "Deactivate / reactivate is not yet supported (no backend endpoint). " +
              "TODO: add PATCH /api/client/{id}/status."
          );
          setDeactivateTarget(null);
        }}
        okText={deactivateTarget?.isActive ? "Deactivate" : "Reactivate"}
        okButtonProps={{ danger: deactivateTarget?.isActive }}
        styles={MODAL_STYLES}
      >
        <p>
          {deactivateTarget?.isActive
            ? `This will mark "${deactivateTarget?.name}" as inactive. You can reactivate it later.`
            : `This will reactivate "${deactivateTarget?.name}".`}
        </p>
      </Modal>
    </div>
  );
};
